import { useEffect, useState, Fragment, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useWorkoutHistoryLimit } from '../hooks/useSubscription';
import PricingModal from './PricingModal';

import DumbbellIcon from '../assets/Dumbbell Default.png';
import CalendarIcon from '../assets/Calendar Default.png';
import LineGraphIcon from '../assets/Line Graph Default.png';
import WorkoutHistoryIcon from '../assets/Workout History Default.png';

export default function WorkoutHistory({ user }) {
  const historyLimit = useWorkoutHistoryLimit(user);
  const [showPricing, setShowPricing] = useState(false);
  const [workouts, setWorkouts] = useState([]);
  const [personalBests, setPersonalBests] = useState({});
  const [progressData, setProgressData] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState('');
  const [consistencyData, setConsistencyData] = useState([]);
  const [progressDataIndex, setProgressDataIndex] = useState(0);
  const [consistencyDataIndex, setConsistencyDataIndex] = useState(0);
  const POINTS_PER_VIEW = 10;

  const prepareProgressData = useCallback((data, exerciseName) => {
    const filtered = data
      .filter((w) => w.exercise === exerciseName && w.weight && w.completed)
      .map((w) => ({ date: w.date, weight: w.weight }));
    setProgressData(filtered);
    setProgressDataIndex(0);
  }, []);

  const calculatePersonalBests = useCallback((data) => {
    const bests = {};
    data.forEach((w) => {
      if (!w.exercise || !w.weight || !w.completed) return;
      const name = w.exercise;
      if (!bests[name] || w.weight > bests[name].weight) {
        bests[name] = {
          weight: w.weight,
          date: w.date,
        };
      }
    });
    setPersonalBests(bests);
    setSelectedExercise(Object.keys(bests)[0] || '');
    prepareProgressData(data, Object.keys(bests)[0] || '');
  }, [prepareProgressData]);

  const calculateConsistency = useCallback((data) => {
    const map = {};
    data.forEach((w) => {
      if (!w.completed) return;
      const day = w.date;
      map[day] = (map[day] || 0) + 1;
    });
    const entries = Object.entries(map).map(([date, count]) => ({ date, count }));
    setConsistencyData(entries);
    setConsistencyDataIndex(0);
  }, []);

  const fetchWorkoutHistory = useCallback(async () => {
    let query = supabase
      .from('workouts')
      .select('*')
      .eq('user_id', user.id);

    // Apply date limit for free users
    if (historyLimit.hasLimit && historyLimit.limitDate) {
      query = query.gte('date', historyLimit.limitDate.toISOString().split('T')[0]);
    }

    const { data, error } = await query.order('date', { ascending: true });

    if (error) {
      console.error('Error fetching workout history:', error);
      return;
    }

    setWorkouts(data);
    calculatePersonalBests(data);
    calculateConsistency(data);
  }, [user.id, historyLimit.limitDate, historyLimit.hasLimit, calculatePersonalBests, calculateConsistency]);

  useEffect(() => {
    if (!user?.id) return;
    fetchWorkoutHistory();
  }, [user, fetchWorkoutHistory]);

  const exerciseOptions = Object.keys(personalBests);

  return (
    <div className="space-y-8">
      {/* Upgrade Banner for Free Users */}
      {historyLimit.hasLimit && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-yellow-200 font-semibold"><img src={WorkoutHistoryIcon} alt="History" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3" /> Limited History</div>
              <div className="text-yellow-300 text-sm">
                Viewing last 30 days only. Upgrade for full history access.
              </div>
            </div>
            <button
              onClick={() => setShowPricing(true)}
              className="bg-yellow-500/80 hover:bg-yellow-500 text-white px-4 py-2 rounded-lg font-semibold transition-all"
            >
              Upgrade
            </button>
          </div>
        </div>
      )}

      {/* Personal Bests */}
      <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
          <img src={DumbbellIcon} alt="Dumbbell" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>Personal Bests
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(personalBests).map(([exercise, info]) => (
            <div
              key={exercise}
              className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg text-white"
            >
              <div className="font-semibold text-lg">{exercise}</div>
              <div className="text-sm">{info.weight} kg on {new Date(info.date).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Progress Tracker */}
      {selectedExercise && (
        <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white flex items-center"><img src={LineGraphIcon} alt="Graph" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Progress Tracker</h2>
            <Listbox value={selectedExercise} onChange={(value) => {
              setSelectedExercise(value);
              prepareProgressData(workouts, value);
            }}>
              <div className="relative w-60">
                <Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white/10 py-2 pl-3 pr-10 text-left text-white border border-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500">
                  <span className="block truncate">{selectedExercise}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-white" aria-hidden="true" />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-lg bg-slate-800 border border-white/20 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
                    {exerciseOptions.map((exercise, index) => (
                      <Listbox.Option
                        key={index}
                        value={exercise}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-purple-600 text-white' : 'text-purple-200'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                            >
                              {exercise}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
          {progressData.length > POINTS_PER_VIEW && (
            <div className="flex items-center justify-center gap-4 mb-4">
              <button
                onClick={() => setProgressDataIndex(Math.max(0, progressDataIndex - POINTS_PER_VIEW))}
                disabled={progressDataIndex === 0}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 disabled:bg-white/5 disabled:text-gray-500 text-white rounded-lg transition-all"
              >
                â† Previous
              </button>
              <span className="text-white text-sm">
                Showing {progressDataIndex + 1}-{Math.min(progressDataIndex + POINTS_PER_VIEW, progressData.length)} of {progressData.length}
              </span>
              <button
                onClick={() => setProgressDataIndex(Math.min(progressData.length - POINTS_PER_VIEW, progressDataIndex + POINTS_PER_VIEW))}
                disabled={progressDataIndex + POINTS_PER_VIEW >= progressData.length}
                className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 disabled:bg-white/5 disabled:text-gray-500 text-white rounded-lg transition-all"
              >
                Next â†’
              </button>
            </div>
          )}
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={progressData.slice(progressDataIndex, progressDataIndex + POINTS_PER_VIEW)} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#666" />
              <XAxis dataKey="date" stroke="#ccc" />
              <YAxis stroke="#ccc" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="weight" stroke="#8884d8" activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Workout Consistency */}
      <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center"><img src={CalendarIcon} alt="Calendar" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Workout Consistency</h2>
        {consistencyData.length > POINTS_PER_VIEW && (
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              onClick={() => setConsistencyDataIndex(Math.max(0, consistencyDataIndex - POINTS_PER_VIEW))}
              disabled={consistencyDataIndex === 0}
              className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 disabled:bg-white/5 disabled:text-gray-500 text-white rounded-lg transition-all"
            >
              â† Previous
            </button>
            <span className="text-white text-sm">
              Showing {consistencyDataIndex + 1}-{Math.min(consistencyDataIndex + POINTS_PER_VIEW, consistencyData.length)} of {consistencyData.length}
            </span>
            <button
              onClick={() => setConsistencyDataIndex(Math.min(consistencyData.length - POINTS_PER_VIEW, consistencyDataIndex + POINTS_PER_VIEW))}
              disabled={consistencyDataIndex + POINTS_PER_VIEW >= consistencyData.length}
              className="px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 disabled:bg-white/5 disabled:text-gray-500 text-white rounded-lg transition-all"
            >
              Next â†’
            </button>
          </div>
        )}
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={consistencyData.slice(consistencyDataIndex, consistencyDataIndex + POINTS_PER_VIEW)} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#666" />
            <XAxis dataKey="date" stroke="#ccc" />
            <YAxis stroke="#ccc" allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#00C49F" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        user={user}
      />
    </div>
  );
}